/**
 * The expanded analytics view.
 *
 * The chart itself is chosen by the server from the user's question and the
 * real column types (see build_chart_payload in Alex_main.py) and drawn by
 * SmartChart. This component's job is the surrounding frame: say why that
 * chart was picked, let the user overrule any part of it, and keep the rows
 * available underneath.
 *
 * It used to dispatch to One/Two/Three/MultiColumnComponent purely on how many
 * columns the result had, which is why anything with four or more never got a
 * chart. Those four files are no longer imported anywhere.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Box, Typography, Button, Paper, Chip, Alert, Divider,
  FormControl, InputLabel, Select, MenuItem, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartChart from './SmartChart';
import { EnhancedTable } from './EnhancedTable';
import { inferColumnMeta, inferSpec } from '../utils/chartData';

const CHART_OPTIONS = [
  { value: 'bar', label: 'Bar' },
  { value: 'horizontal_bar', label: 'Horizontal bar' },
  { value: 'grouped_bar', label: 'Grouped bar' },
  { value: 'stacked_bar', label: 'Stacked bar' },
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
  { value: 'pie', label: 'Pie' },
  { value: 'donut', label: 'Donut' },
  { value: 'scatter', label: 'Scatter' },
  { value: 'kpi', label: 'Single figure' },
  { value: 'table', label: 'Table only' },
];

const AGG_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count of rows' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

const BAR_LIKE = ['bar', 'horizontal_bar', 'grouped_bar', 'stacked_bar'];
const prettify = (name) => String(name || '').replace(/_/g, ' ');

const AnalyticsPopup = ({ open, data, title, spec, columns, onClose }) => {
  const [override, setOverride] = useState({});

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const columnMeta = useMemo(
    () => (Array.isArray(columns) && columns.length ? columns : inferColumnMeta(rows)),
    [columns, rows],
  );

  // Messages replayed from IndexedDB predate the chart spec, so fall back to
  // the same deterministic pick the server uses when its model is unavailable.
  const baseSpec = useMemo(
    () => (spec && spec.type ? spec : inferSpec(rows, columnMeta)),
    [spec, rows, columnMeta],
  );

  useEffect(() => {
    if (open) setOverride({});
  }, [open, baseSpec]);

  const activeSpec = useMemo(() => ({ ...baseSpec, ...override }), [baseSpec, override]);

  if (!open) return null;

  const dimensions = columnMeta.filter((column) => column.role !== 'measure');
  const measures = columnMeta.filter((column) => column.role === 'measure');
  const columnNames = columnMeta.map((column) => column.name);
  const columnTypes = columnMeta.reduce((acc, column) => {
    acc[column.name] = column.role === 'measure'
      ? 'numeric'
      : column.role === 'temporal' ? 'datetime' : 'categorical';
    return acc;
  }, {});

  const isCount = activeSpec.agg === 'count';
  const showsChart = activeSpec.type !== 'table';
  const isEdited = Object.keys(override).length > 0;
  const update = (patch) => setOverride((prev) => ({ ...prev, ...patch }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="analytics-modal-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
    >
      <Paper
        sx={(theme) => ({
          width: '95%',
          maxWidth: 1200,
          maxHeight: '92vh',
          overflow: 'auto',
          p: 3,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ pr: 2 }}>
            <Typography id="analytics-modal-title" variant="h6" component="h2">
              {activeSpec.title || title || 'Data Analytics'}
            </Typography>
            {activeSpec.reason && !isEdited && (
              <Typography variant="caption" color="text.secondary">
                Chosen because {activeSpec.reason}
              </Typography>
            )}
          </Box>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 1 }} color="inherit">
            <CloseIcon />
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {!rows.length ? (
          <Alert severity="warning">This result came back empty, so there is nothing to chart.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" label={`${rows.length.toLocaleString()} rows`} />
              <Chip size="small" label={`${columnNames.length} columns`} />
              {measures.length > 0 && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Measures: ${measures.map((column) => prettify(column.name)).join(', ')}`}
                />
              )}
            </Stack>

            {showsChart && (
              <Box sx={{ mb: 2 }}>
                <SmartChart spec={activeSpec} rows={rows} height={420} showTitle={false} />
              </Box>
            )}

            {/* Overrides: the server's pick is usually right, but it is still a
                pick, and a wrong one should cost a dropdown, not a re-query. */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="chart-type-label">Chart</InputLabel>
                <Select
                  labelId="chart-type-label"
                  label="Chart"
                  value={activeSpec.type || 'bar'}
                  onChange={(event) => update({ type: event.target.value })}
                >
                  {CHART_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showsChart && activeSpec.type !== 'kpi' && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="chart-x-label">Group by</InputLabel>
                  <Select
                    labelId="chart-x-label"
                    label="Group by"
                    value={columnNames.includes(activeSpec.x) ? activeSpec.x : ''}
                    onChange={(event) => update({ x: event.target.value })}
                  >
                    {columnNames.map((name) => (
                      <MenuItem key={name} value={name}>{prettify(name)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {showsChart && !isCount && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="chart-y-label">Measure</InputLabel>
                  <Select
                    labelId="chart-y-label"
                    label="Measure"
                    value={(activeSpec.y || [])[0] || ''}
                    onChange={(event) => update({ y: [event.target.value] })}
                  >
                    {(measures.length ? measures : columnMeta).map((column) => (
                      <MenuItem key={column.name} value={column.name}>{prettify(column.name)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {showsChart && activeSpec.type !== 'scatter' && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="chart-agg-label">Aggregate</InputLabel>
                  <Select
                    labelId="chart-agg-label"
                    label="Aggregate"
                    value={activeSpec.agg || 'sum'}
                    onChange={(event) => update({ agg: event.target.value })}
                  >
                    {AGG_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {[...BAR_LIKE, 'pie', 'donut'].includes(activeSpec.type) && (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="chart-topn-label">Show top</InputLabel>
                  <Select
                    labelId="chart-topn-label"
                    label="Show top"
                    value={activeSpec.top_n || 12}
                    onChange={(event) => update({ top_n: Number(event.target.value) })}
                  >
                    {[5, 10, 12, 20, 30, 50].map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {[...BAR_LIKE, 'line', 'area'].includes(activeSpec.type) && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="chart-series-label">Split by</InputLabel>
                  <Select
                    labelId="chart-series-label"
                    label="Split by"
                    value={activeSpec.series || ''}
                    onChange={(event) => update({ series: event.target.value || null })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {dimensions
                      .filter((column) => column.name !== activeSpec.x)
                      .map((column) => (
                        <MenuItem key={column.name} value={column.name}>{prettify(column.name)}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {isEdited && <Button size="small" onClick={() => setOverride({})}>Reset</Button>}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Underlying rows{rows.length > 200 ? ' (first 200)' : ''}
            </Typography>
            <EnhancedTable
              data={rows.slice(0, 200)}
              columns={columnNames}
              columnTypes={columnTypes}
              maxHeight={320}
            />
          </>
        )}

        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="contained">Close</Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default AnalyticsPopup;
